import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Button,
  TextField,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Publish as PublishIcon,
  Code as CodeIcon,
  Folder as FolderIcon,
  Public as PublicIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';

import { nodeEditorService } from '../../services/nodeEditorService';
import { NodeEditorSession } from '../../types/nodeEditor.types';

interface SessionListProps {
  onSelectSession: (sessionId: string) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, index, value }) => {
  return value === index ? <Box sx={{ p: 2 }}>{children}</Box> : null;
};

export const SessionList: React.FC<SessionListProps> = ({ onSelectSession }) => {
  const [sessions, setSessions] = useState<NodeEditorSession[]>([]);
  const [mySessions, setMySessions] = useState<NodeEditorSession[]>([]);
  const [publishedSessions, setPublishedSessions] = useState<NodeEditorSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedSession, setSelectedSession] = useState<NodeEditorSession | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  useEffect(() => {
    loadSessions();
  }, []);
  
  const loadSessions = async () => {
    setLoading(true);
    try {
      const [all, my, published] = await Promise.all([
        nodeEditorService.getSessions(),
        nodeEditorService.getMySessions(),
        nodeEditorService.getPublishedSessions(),
      ]);
      setSessions(all);
      setMySessions(my);
      setPublishedSessions(published);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!selectedSession) return;
    
    try {
      await nodeEditorService.deleteSession(selectedSession.id);
      setShowDeleteDialog(false);
      setMenuAnchor(null);
      loadSessions();
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'success';
      case 'editing': return 'primary';
      case 'draft': return 'default';
      default: return 'default';
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };
  
  const renderSessionList = (sessionList: NodeEditorSession[]) => {
    const filtered = sessionList.filter(s =>
      (s.node_display_name || s.node?.display_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.node_name || s.node?.name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    if (filtered.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <FolderIcon sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
          <Typography variant="body1" color="textSecondary">
            No sessions found
          </Typography>
        </Box>
      );
    }
    
    return (
      <List>
        {filtered.map((session) => (
          <Paper key={session.id} sx={{ mb: 2 }}>
            <ListItem
              button
              onClick={() => onSelectSession(session.id)}
              sx={{ py: 2 }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: session.is_published ? 'success.light' : 'primary.light' }}>
                  {session.is_published ? <PublicIcon /> : <CodeIcon />}
                </Avatar>
              </ListItemAvatar>
              
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1">
                      {session.node_display_name || session.node?.display_name || 'New Node'}
                    </Typography>
                    <Chip
                      size="small"
                      label={session.status}
                      color={getStatusColor(session.status) as any}
                    />
                    {session.is_published && (
                      <Chip
                        size="small"
                        label="Published"
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="textSecondary">
                      Category: {session.node_category || session.node?.category || 'action'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                      <Typography variant="caption" color="textSecondary">
                        <TimeIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                        {formatDate(session.last_activity)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        by {session.user_name}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
              
              <ListItemSecondaryAction>
                <IconButton
                  onClick={(e) => {
                    setSelectedSession(session);
                    setMenuAnchor(e.currentTarget);
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          </Paper>
        ))}
      </List>
    );
  };
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Node Editor Sessions</Typography>
        </Box>
        
        <TextField
          fullWidth
          size="small"
          placeholder="Search sessions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>
      
      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
        <Tab label="All Sessions" />
        <Tab label="My Sessions" />
        <Tab label="Published" />
      </Tabs>
      
      {/* Session Lists */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <TabPanel value={activeTab} index={0}>
          {renderSessionList(sessions)}
        </TabPanel>
        
        <TabPanel value={activeTab} index={1}>
          {renderSessionList(mySessions)}
        </TabPanel>
        
        <TabPanel value={activeTab} index={2}>
          {renderSessionList(publishedSessions)}
        </TabPanel>
      </Box>
      
      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => {
          if (selectedSession) onSelectSession(selectedSession.id);
          setMenuAnchor(null);
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Open
        </MenuItem>
        
        {selectedSession?.permissions?.can_delete_own_nodes && (
          <MenuItem
            onClick={() => {
              setShowDeleteDialog(true);
              setMenuAnchor(null);
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
      
      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Session</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedSession?.node_display_name || selectedSession?.node?.display_name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SessionList;
